// v1
// Path: frontend/src/components/QuickBooksSync/ConnectionLogsModal.jsx
// Inputs: token, accountId, connectionTitle, onClose
// Outputs: React JSX modal displaying execution audit history for a specific connection
// Summary: Connection-specific audit log viewer showing record mutations, timestamps, status badges, and error diagnostics.

import React, { useState, useEffect } from 'react';


// Auto-injected unbox helper for standardized API responses
const unbox = async (res) => {
    const payload = await res.json();
    if (payload && typeof payload === 'object' && payload.success === true && payload.data !== undefined) {
        return payload.data;
    }
    return payload;
};

export const ConnectionLogsModal = ({ token, accountId, connectionTitle, onClose }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedLogId, setExpandedLogId] = useState(null);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/sync/sync/logs', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await unbox(res);
                const filtered = accountId 
                    ? data.filter(l => String(l.AccountID) === String(accountId))
                    : data;
                setLogs(filtered);
            }
        } catch (err) {
            console.error('[ConnectionLogsModal] Fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [token, accountId]);

    const getStatusBadge = (status) => {
        switch (status) {
            case 'SUCCESS':
                return 'bg-emerald-950 text-emerald-300 border-emerald-800';
            case 'WARNING':
                return 'bg-amber-950 text-amber-300 border-amber-800';
            case 'FAILED':
                return 'bg-red-950 text-red-300 border-red-800';
            default:
                return 'bg-slate-900 text-slate-400 border-slate-700';
        }
    };

    return (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="p-5 border-b border-slate-700 flex justify-between items-center bg-slate-800/90">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-600/20 border border-blue-500/40 flex items-center justify-center text-blue-400">
                            <span className="material-symbols-outlined">receipt_long</span>
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-white flex items-center gap-2">
                                Audit Logs: <span className="text-blue-300">{connectionTitle || 'Connection'}</span>
                            </h3>
                            <p className="text-xs text-slate-400">Execution history and batch synchronization results</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={fetchLogs}
                            disabled={loading}
                            className="p-1.5 text-slate-400 hover:text-white rounded hover:bg-slate-700 transition-colors"
                            title="Refresh logs"
                        >
                            <span className={`material-symbols-outlined text-[18px] ${loading ? 'animate-spin' : ''}`}>sync</span>
                        </button>
                        <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded">
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-5 overflow-y-auto flex-1 space-y-3">
                    {loading ? (
                        <div className="p-12 text-center text-slate-400">
                            <span className="material-symbols-outlined animate-spin text-3xl mb-2 text-blue-400">sync</span>
                            <p>Loading audit history...</p>
                        </div>
                    ) : logs.length === 0 ? (
                        <div className="bg-slate-900/50 border border-slate-700/60 rounded-lg p-10 text-center text-slate-400">
                            <span className="material-symbols-outlined text-4xl text-slate-500 mb-2">history</span>
                            <h4 className="text-slate-300 font-medium">No Execution Logs Recorded Yet</h4>
                            <p className="text-xs text-slate-500 mt-1">Logs will appear here once live batch synchronizations are triggered for this connection.</p>
                        </div>
                    ) : (
                        <div className="border border-slate-700 rounded-lg overflow-hidden">
                            <table className="w-full text-left text-xs text-slate-300">
                                <thead className="bg-slate-900/90 text-slate-400 font-semibold border-b border-slate-700">
                                    <tr>
                                        <th className="p-3">Timestamp</th>
                                        <th className="p-3">Entity</th>
                                        <th className="p-3">Status</th>
                                        <th className="p-3">Read</th>
                                        <th className="p-3">Inserted</th>
                                        <th className="p-3">Updated</th>
                                        <th className="p-3">Failed</th>
                                        <th className="p-3 text-right">Details</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700/60">
                                    {logs.map(log => {
                                        const isExpanded = expandedLogId === log.LogID;
                                        return (
                                            <React.Fragment key={log.LogID}>
                                                <tr className="hover:bg-slate-750">
                                                    <td className="p-3 font-mono text-slate-400">
                                                        {new Date(log.ExecutedAt).toLocaleString()}
                                                    </td>
                                                    <td className="p-3 font-semibold text-white">
                                                        {log.EntityType}
                                                    </td>
                                                    <td className="p-3">
                                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${getStatusBadge(log.Status)}`}>
                                                            {log.Status}
                                                        </span>
                                                    </td>
                                                    <td className="p-3 font-mono">{log.RecordsRead}</td>
                                                    <td className="p-3 font-mono text-emerald-400 font-medium">+{log.RecordsInserted}</td>
                                                    <td className="p-3 font-mono text-blue-400 font-medium">{log.RecordsUpdated}</td>
                                                    <td className="p-3 font-mono text-red-400 font-medium">{log.RecordsFailed}</td>
                                                    <td className="p-3 text-right">
                                                        <button
                                                            onClick={() => setExpandedLogId(isExpanded ? null : log.LogID)}
                                                            className="text-blue-400 hover:underline inline-flex items-center gap-0.5"
                                                        >
                                                            {isExpanded ? 'Hide' : 'View'}
                                                            <span className="material-symbols-outlined text-[14px]">
                                                                {isExpanded ? 'expand_less' : 'expand_more'}
                                                            </span>
                                                        </button>
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr className="bg-slate-900/80">
                                                        <td colSpan="8" className="p-4 space-y-2 border-t border-slate-800">
                                                            <div className="text-xs text-slate-300 font-medium">
                                                                Summary: {log.Summary || 'No summary text'}
                                                            </div>
                                                            {log.DetailsJson && (
                                                                <pre className="p-3 bg-slate-950 rounded text-[11px] font-mono text-slate-300 overflow-x-auto max-h-48 border border-slate-800">
                                                                    {log.DetailsJson}
                                                                </pre>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-700 flex justify-end bg-slate-800/90">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded text-sm transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};
